/**
 * Post Execution Feedback Modal
 * Shows feedback prompt after action execution
 */

import React, { useEffect, useState } from 'react';
import { View, Text, Pressable, Modal, Platform, TextInput } from 'react-native';
import { AntDesign, MaterialIcons } from '@expo/vector-icons';
import * as Animatable from 'react-native-animatable';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';

interface PostExecutionFeedbackProps {
  visible: boolean;
  actionDescription: string;
  onFeedback: (feedbackType: 'upvote' | 'downvote', comment?: string) => void;
  onDismiss: () => void;
  autoDismissAfter?: number; // Auto-dismiss after X seconds (default: 5)
}

const PostExecutionFeedback: React.FC<PostExecutionFeedbackProps> = ({
  visible,
  actionDescription,
  onFeedback,
  onDismiss,
  autoDismissAfter = 5000
}) => {
  const theme = themeSelector();
  const [comment, setComment] = useState('');
  const [feedbackGiven, setFeedbackGiven] = useState(false);

  // Auto-dismiss after specified time
  useEffect(() => {
    if (visible && autoDismissAfter > 0) {
      const timer = setTimeout(() => {
        if (!feedbackGiven) {
          onDismiss();
        }
      }, autoDismissAfter);

      return () => clearTimeout(timer);
    }
  }, [visible, autoDismissAfter, feedbackGiven, onDismiss]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!visible) {
      setComment('');
      setFeedbackGiven(false);
    }
  }, [visible]);

  const handleFeedback = (feedbackType: 'upvote' | 'downvote') => {
    setFeedbackGiven(true);
    onFeedback(feedbackType, comment.trim() || undefined);
    
    // Auto-dismiss after feedback
    setTimeout(() => {
      onDismiss();
    }, 1000);
  };

  const handleDismiss = () => {
    setFeedbackGiven(true);
    onDismiss();
  };

  if (!visible) {
    return null;
  }

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={handleDismiss}
    >
      <View style={{
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        padding: 20
      }}>
        <Animatable.View
          animation="zoomIn"
          duration={300}
          style={{
            backgroundColor: theme ? '#ffffff' : '#1e293b',
            borderRadius: 16,
            padding: 24,
            minWidth: Platform.OS === 'web' ? 400 : '85%',
            maxWidth: Platform.OS === 'web' ? 500 : '95%',
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.3,
            shadowRadius: 12,
            elevation: 8
          }}
        >
          {/* Header */}
          <View style={{
            flexDirection: 'row',
            alignItems: 'center',
            marginBottom: 16
          }}>
            <MaterialIcons 
              name="feedback" 
              size={24} 
              color={theme ? '#3b82f6' : '#60a5fa'} 
              style={{ marginRight: 12 }}
            />
            <Text style={{
              fontSize: 18,
              fontWeight: '700',
              color: theme ? '#1f2937' : '#f3f4f6',
              flex: 1
            }}>
              Was this helpful?
            </Text>
          </View>

          {/* Action description */}
          <Text style={{
            fontSize: 14,
            color: theme ? '#6b7280' : '#9ca3af',
            marginBottom: 20,
            lineHeight: 20
          }}>
            You just executed: <Text style={{ fontWeight: '600', color: theme ? '#374151' : '#e5e7eb' }}>
              {actionDescription}
            </Text>
          </Text>

          {/* Feedback buttons */}
          <View style={{
            flexDirection: 'row',
            justifyContent: 'center',
            gap: 20,
            marginBottom: 20
          }}>
            <Pressable
              onPress={() => handleFeedback('upvote')}
              style={({ pressed }) => ({
                flexDirection: 'row',
                alignItems: 'center',
                backgroundColor: pressed ? 'rgba(34, 197, 94, 0.1)' : 'rgba(34, 197, 94, 0.05)',
                borderWidth: 1,
                borderColor: '#22c55e',
                borderRadius: 12,
                paddingHorizontal: 20,
                paddingVertical: 12,
                opacity: pressed ? 0.8 : 1
              })}
            >
              <AntDesign name="like" size={20} color="#22c55e" style={{ marginRight: 8 }} />
              <Text style={{
                fontSize: 16,
                fontWeight: '600',
                color: '#22c55e'
              }}>
                Yes, helpful
              </Text>
            </Pressable>

            <Pressable
              onPress={() => handleFeedback('downvote')}
              style={({ pressed }) => ({
                flexDirection: 'row',
                alignItems: 'center',
                backgroundColor: pressed ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.05)',
                borderWidth: 1,
                borderColor: '#ef4444',
                borderRadius: 12,
                paddingHorizontal: 20,
                paddingVertical: 12,
                opacity: pressed ? 0.8 : 1
              })}
            >
              <AntDesign name="dislike" size={20} color="#ef4444" style={{ marginRight: 8 }} />
              <Text style={{
                fontSize: 16,
                fontWeight: '600',
                color: '#ef4444'
              }}>
                Not helpful
              </Text>
            </Pressable>
          </View>

          {/* Optional comment */}
          <View style={{ marginBottom: 20 }}>
            <Text style={{
              fontSize: 14,
              fontWeight: '600',
              color: theme ? '#374151' : '#e5e7eb',
              marginBottom: 8
            }}>
              Optional: Tell us more (optional)
            </Text>
            <TextInput
              value={comment}
              onChangeText={setComment}
              placeholder="What could be improved? Any suggestions?"
              placeholderTextColor={theme ? '#9ca3af' : '#6b7280'}
              multiline
              numberOfLines={3}
              style={{
                backgroundColor: theme ? '#f9fafb' : '#374151',
                borderWidth: 1,
                borderColor: theme ? '#e5e7eb' : '#4b5563',
                borderRadius: 8,
                padding: 12,
                fontSize: 14,
                color: theme ? '#1f2937' : '#f3f4f6',
                textAlignVertical: 'top',
                minHeight: 80
              }}
            />
          </View>

          {/* Footer */}
          <View style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Text style={{
              fontSize: 12,
              color: theme ? '#9ca3af' : '#6b7280',
              flex: 1
            }}>
              Auto-dismisses in {Math.ceil(autoDismissAfter / 1000)}s
            </Text>

            <Pressable
              onPress={handleDismiss}
              style={({ pressed }) => ({
                paddingHorizontal: 16,
                paddingVertical: 8,
                borderRadius: 8,
                backgroundColor: pressed ? (theme ? '#f3f4f6' : '#374151') : 'transparent',
                opacity: pressed ? 0.7 : 1
              })}
            >
              <Text style={{
                fontSize: 14,
                color: theme ? '#6b7280' : '#9ca3af',
                fontWeight: '500'
              }}>
                Skip
              </Text>
            </Pressable>
          </View>
        </Animatable.View>
      </View>
    </Modal>
  );
};

export default PostExecutionFeedback;
